/**
 * Barra annunci a scorrimento — versione trascinabile col mouse/touch.
 *
 * Fino a quando l'utente non interagisce, la barra scorre da sola
 * (stessa velocità impostata nel pannello sezione). Cliccando e
 * trascinando (mouse o dito) si può fermare, spostare avanti/indietro
 * a piacere; al rilascio riprende a scorrere da dove è stata lasciata.
 *
 * Se prefers-reduced-motion è attivo, questo script non parte affatto:
 * la barra resta statica e scrollabile in modo nativo (vedi il CSS).
 *
 * La barra è in position:fixed (vedi CSS), quindi questo script spinge
 * giù il resto della pagina (margin-top sul body) di un'altezza pari
 * alla barra stessa, e la ricalcola ogni volta che la barra cambia
 * altezza (es. passaggio al breakpoint mobile).
 */
(function () {
  function syncBodyOffset(bar) {
    var height = bar.getBoundingClientRect().height;
    document.body.style.marginTop = height + 'px';
    // Temi Shopify basati su Dawn (come il tuo) hanno un header con una
    // propria logica "sticky" che si aggancia sempre a top:0 quando scorri,
    // ignorando la nostra barra. Pubblichiamo l'altezza reale della barra
    // come variabile CSS: il file .css la usa per spostare il punto di
    // aggancio dell'header subito sotto di noi (vedi regola .section-header
    // / .shopify-section-header-sticky nel CSS). Su temi diversi da Dawn
    // questa variabile semplicemente non viene usata da nessuno: innocua.
    document.documentElement.style.setProperty('--marquee-bar-height', height + 'px');
  }

  function initMarqueeBar(bar) {
    if (bar.dataset.marqueeInitialized === 'true') return;

    var track = bar.querySelector('.marquee-bar__track');
    var firstContent = bar.querySelector('.marquee-bar__content');
    var scrollEl = bar.querySelector('.marquee-bar__scroll');
    if (!track || !firstContent) return;

    // lo spazio in alto va compensato anche se prefers-reduced-motion
    // è attivo (la barra fixed resta comunque lì), quindi lo facciamo
    // subito, prima dell'eventuale uscita anticipata qui sotto.
    syncBodyOffset(bar);
    var offsetResizeHandler = function () {
      syncBodyOffset(bar);
    };
    window.addEventListener('resize', offsetResizeHandler);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // rispetta la preferenza di sistema: nessuna animazione automatica,
      // nessun drag custom, resta l'overflow-x nativo definito nel CSS.
      bar.__marqueeTeardown = function () {
        window.removeEventListener('resize', offsetResizeHandler);
      };
      return;
    }

    bar.dataset.marqueeInitialized = 'true';

    // da qui in poi il movimento è gestito interamente da JS, quindi
    // spegniamo l'animazione CSS (che resta come fallback se lo script
    // non arriva a eseguirsi, es. errore di rete o blocco).
    track.style.animation = 'none';

    var speedSetting = parseFloat(bar.style.getPropertyValue('--marquee-speed')) || 25; // secondi per ciclo

    // larghezza "naturale" di UN passaggio dei tuoi messaggi, misurata PRIMA
    // di aggiungere qualunque copia di riempimento: è quella che determina
    // la velocità percepita. Se usassimo invece la larghezza già riempita
    // (che può essere multipla, vedi ensureSeamlessWidth più sotto), la
    // barra sembrerebbe accelerare da sola ogni volta che serve più spazio
    // da riempire — motivo per cui prima girava troppo veloce.
    var naturalUnitWidth = firstContent.getBoundingClientRect().width;

    var contentWidth = 0; // larghezza reale del "giro" (può includere copie di riempimento)
    var pxPerSecond = 0;
    var position = 0; // offset corrente in px (sempre <= 0)

    var isDragging = false;
    var dragStartX = 0;
    var dragStartPosition = 0;
    var dragDistance = 0;

    var lastTimestamp = null;
    var paused = false;
    var rafId = null;

    // Con pochi messaggi corti, una "copia" del contenuto (firstContent) può
    // essere molto più stretta della barra: il loop resterebbe comunque
    // matematicamente perfetto, ma per gran parte del tempo si vedrebbe solo
    // spazio vuoto, con i messaggi che sembrano "sparire e poi ricaricarsi
    // tutti insieme" quando il giro si ripete. La soluzione è duplicare il
    // contenuto (con copie invisibili a screen reader) finché una singola
    // copia non è larga almeno quanto lo spazio visibile della barra.
    function ensureSeamlessWidth() {
      var contents = bar.querySelectorAll('.marquee-bar__content');
      if (!contents.length) return;
      var viewportWidth = scrollEl ? scrollEl.getBoundingClientRect().width : window.innerWidth;
      var target = viewportWidth + 40; // piccolo margine di sicurezza
      var guard = 0; // paracadute anti-loop-infinito, non dovrebbe mai servire
      while (firstContent.getBoundingClientRect().width < target && guard < 20) {
        for (var i = 0; i < contents.length; i++) {
          var contentEl = contents[i];
          var originalChildren = Array.prototype.slice.call(contentEl.children);
          if (!originalChildren.length) continue;
          originalChildren.forEach(function (child) {
            var clone = child.cloneNode(true);
            // le copie di riempimento sono puramente decorative: invisibili
            // a screen reader e non raggiungibili da tastiera.
            clone.setAttribute('aria-hidden', 'true');
            if (clone.tagName === 'A') clone.setAttribute('tabindex', '-1');
            contentEl.appendChild(clone);
          });
        }
        guard++;
      }
    }

    function measure() {
      ensureSeamlessWidth();
      contentWidth = firstContent.getBoundingClientRect().width;
      var baseWidth = naturalUnitWidth > 0 ? naturalUnitWidth : contentWidth;
      pxPerSecond = speedSetting > 0 && baseWidth > 0 ? baseWidth / speedSetting : 0;
    }

    function normalize(pos) {
      if (contentWidth <= 0) return pos;
      while (pos <= -contentWidth) pos += contentWidth;
      while (pos > 0) pos -= contentWidth;
      return pos;
    }

    function applyTransform() {
      track.style.transform = 'translateX(' + position + 'px)';
    }

    function tick(timestamp) {
      if (lastTimestamp === null) lastTimestamp = timestamp;
      var delta = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      if (!isDragging && !paused) {
        position = normalize(position - pxPerSecond * delta);
        applyTransform();
      }
      rafId = requestAnimationFrame(tick);
    }

    function getClientX(e) {
      return e.touches && e.touches.length ? e.touches[0].clientX : e.clientX;
    }

    function onPointerDown(e) {
      isDragging = true;
      dragDistance = 0;
      track.classList.add('is-dragging');
      dragStartX = getClientX(e);
      dragStartPosition = position;
      if (!e.touches) e.preventDefault();
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      var delta = getClientX(e) - dragStartX;
      dragDistance = Math.abs(delta);
      position = normalize(dragStartPosition + delta);
      applyTransform();
    }

    function onPointerUp() {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('is-dragging');
    }

    // se l'utente ha trascinato (anche solo un po'), il click successivo
    // sul link non deve aprire la pagina: era un gesto di trascinamento,
    // non un tap sul messaggio.
    function onClickCapture(e) {
      if (dragDistance > 5) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    function onDragStart(e) {
      e.preventDefault();
    }

    function onMouseEnter() {
      paused = true;
    }

    function onMouseLeave() {
      paused = false;
    }

    // Teniamo traccia di ogni listener/observer aggiunti qui, per poterli
    // rimuovere tutti quando la sezione viene ricaricata dal Theme
    // Customizer (es. cambiando un'impostazione come la velocità). Senza
    // questa pulizia, ogni modifica nel pannello lascerebbe un vecchio
    // ciclo di animazione "fantasma" acceso all'infinito sull'elemento
    // rimosso — accumulandosi ad ogni modifica fino a rallentare/bloccare
    // visibilmente la pagina dopo pochi cambi di impostazione.
    var resizeObservers = [];

    track.addEventListener('dragstart', onDragStart);
    track.addEventListener('mousedown', onPointerDown);
    track.addEventListener('touchstart', onPointerDown, { passive: true });
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('touchmove', onPointerMove, { passive: true });
    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchend', onPointerUp);
    track.addEventListener('click', onClickCapture, true);
    bar.addEventListener('mouseenter', onMouseEnter);
    bar.addEventListener('mouseleave', onMouseLeave);

    measure();

    // Il caricamento asincrono del font del tema (molto comune su Shopify)
    // cambia la larghezza reale del testo DOPO la prima misurazione: se non
    // si ricalcola, l'animazione continua a usare una larghezza "vecchia" e
    // il loop si disallinea.
    // ResizeObserver copre in automatico: cambi di font, testo diverso,
    // resize della finestra, rotazione mobile, ecc.
    var fallbackResizeHandler = null;
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () {
        measure();
      });
      ro.observe(firstContent);
      if (scrollEl) ro.observe(scrollEl); // riempie di nuovo se lo spazio visibile cambia (resize, breakpoint icone social)
      resizeObservers.push(ro);
    } else {
      // fallback per browser molto vecchi senza ResizeObserver
      fallbackResizeHandler = measure;
      window.addEventListener('resize', fallbackResizeHandler);
      window.addEventListener('load', fallbackResizeHandler);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }

    rafId = requestAnimationFrame(tick);

    bar.__marqueeTeardown = function () {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', offsetResizeHandler);
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchend', onPointerUp);
      if (fallbackResizeHandler) {
        window.removeEventListener('resize', fallbackResizeHandler);
        window.removeEventListener('load', fallbackResizeHandler);
      }
      resizeObservers.forEach(function (observer) {
        observer.disconnect();
      });
    };
  }

  function initAll() {
    document.querySelectorAll('.marquee-bar').forEach(initMarqueeBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // riattiva la barra quando viene ricaricata dentro il Theme Customizer
  // (es. dopo aver cambiato un'impostazione come la velocità)
  document.addEventListener('shopify:section:load', function (e) {
    var bar = e.target.querySelector && e.target.querySelector('.marquee-bar');
    if (bar) initMarqueeBar(bar);
  });

  // e ripulisce la versione precedente della barra, appena prima che il
  // Theme Customizer la rimuova dalla pagina — evita loop e listener
  // "fantasma" che si accumulano ad ogni modifica nel pannello.
  document.addEventListener('shopify:section:unload', function (e) {
    var bar = e.target.querySelector && e.target.querySelector('.marquee-bar');
    if (bar && typeof bar.__marqueeTeardown === 'function') {
      bar.__marqueeTeardown();
    }
  });
})();